import { defineStore } from 'pinia';
import {
  createOrganization,
  getCurrentUser,
  listOrganizations,
  type Organization,
} from '@/api/organizations';
import { getSelectedOrganizationId, setSelectedOrganizationId } from '@/lib/organization-selection';
import { useSubscriptionStore } from '@/stores/subscription';
import { useToastStore } from '@/stores/toast';

interface State {
  organizations: Organization[];
  defaultOrganizationId: string | null;
  selectedId: string | null;
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

// Bumped on clear() so loads started for a previous account never commit.
let generation = 0;
let pendingLoad: { generation: number; promise: Promise<boolean> } | null = null;

export const useOrganizationStore = defineStore('organization', {
  state: (): State => ({
    organizations: [],
    defaultOrganizationId: null,
    selectedId: getSelectedOrganizationId(),
    loaded: false,
    loading: false,
    error: null,
  }),
  getters: {
    current: (state): Organization | null =>
      state.organizations.find((o) => o.id === state.selectedId) ?? null,
    role(): Organization['role'] | null {
      return this.current?.role ?? null;
    },
    canManageRunners(): boolean {
      return this.role === 'OWNER' || this.role === 'EDITOR';
    },
    canManageOrganization(): boolean {
      return this.role === 'OWNER';
    },
    canManageBilling(): boolean {
      return this.role === 'OWNER';
    },
  },
  actions: {
    select(id: string) {
      if (id === this.selectedId) return;
      this.selectedId = id;
      setSelectedOrganizationId(id);
      useSubscriptionStore().invalidate();
    },
    /**
     * Loads the memberships and keeps the selection valid. Resolves to true when the
     * previous selection was no longer available and the default organization was selected.
     * Concurrent calls for the same account share one request.
     */
    load(): Promise<boolean> {
      if (pendingLoad?.generation === generation) return pendingLoad.promise;
      const entry = { generation, promise: Promise.resolve(false) };
      entry.promise = this._load(generation).finally(() => {
        if (pendingLoad === entry) pendingLoad = null;
      });
      pendingLoad = entry;
      return entry.promise;
    },
    async _load(context: number): Promise<boolean> {
      this.loading = true;
      this.error = null;
      try {
        const [organizations, me] = await Promise.all([listOrganizations(), getCurrentUser()]);
        if (context !== generation) return false;
        const previous = this.current;
        this.organizations = organizations;
        this.defaultOrganizationId =
          me.defaultOrganizationId ?? organizations.find((o) => o.default)?.id ?? null;
        this.loaded = true;
        if (this.selectedId && organizations.some((o) => o.id === this.selectedId)) return false;

        const lostSelection = this.selectedId !== null;
        const fallback =
          organizations.find((o) => o.id === this.defaultOrganizationId) ?? organizations[0];
        if (fallback) {
          this.select(fallback.id);
        } else {
          this.selectedId = null;
          setSelectedOrganizationId(null);
        }
        if (lostSelection && fallback) {
          const name = previous?.name ? `“${previous.name}”` : 'the selected organization';
          useToastStore().show(
            `You no longer have access to ${name}. Switched to “${fallback.name}”.`,
            'info',
            6000
          );
        }
        return lostSelection;
      } catch (e) {
        if (context !== generation) return false;
        this.error = 'Could not load your organizations.';
        throw e;
      } finally {
        if (context === generation) this.loading = false;
      }
    },
    async ensureLoaded() {
      if (!this.loaded) await this.load();
    },
    async create(name: string) {
      const created = await createOrganization(name);
      this.organizations = [...this.organizations, created];
      this.select(created.id);
      return created;
    },
    upsert(organization: Organization) {
      const index = this.organizations.findIndex((o) => o.id === organization.id);
      if (index === -1) {
        this.organizations = [...this.organizations, organization];
      } else {
        this.organizations.splice(index, 1, organization);
      }
    },
    clear() {
      generation++;
      pendingLoad = null;
      this.$reset();
      this.selectedId = null;
      setSelectedOrganizationId(null);
    },
  },
});
